import { useMemo } from 'react';
import { useOrdersDataStore } from '@/stores/ordersDataStore';
import { PageHeader } from '@/components/PageHeader';
import { buildCalendarItems, todayDay, warehouseItems } from '@/features/overview/calendarItems';
import { OverviewCalendar } from '@/features/overview/OverviewCalendar';
import { DiscrepanciesView } from '@/features/orders/DiscrepanciesView';
import { QUEUE_CARD_CLASS } from '@/features/orders/workLib';
import { PageState } from './PageState';

const PANEL = 'overflow-hidden rounded-[10px] border border-border bg-card shadow-card';

/**
 * The warehouse's reference page (`orders:page:warehouse`): what is coming in
 * and what landed, and the count differences open against receivings. Read
 * only — chips explain themselves in a tooltip and open nothing, and the one
 * control past the filters is the count sheet (`orders:page:count_sheet`).
 *
 * Built from the shell's two reads (journeys + overview) and nothing else, so
 * a warehouse account needs only `orders:read:journeys` beside its page codes.
 * Money never reaches it without `orders:read:costs`: the server nulls it and
 * the discrepancies table drops its value column.
 */
export function WarehousePage() {
  const { overview, orders, loading, error, load } = useOrdersDataStore();
  const today = todayDay();
  const items = useMemo(
    // No releases read: street-date release state is a purchasing note the
    // warehouse calendar drops anyway.
    () => warehouseItems(buildCalendarItems({ orders, overview, releases: null, today })),
    [orders, overview, today]
  );
  const eyebrow = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="relative h-full overflow-y-auto bg-background">
      <div className="mx-auto max-w-[1600px] pb-8">
        <PageHeader eyebrow={eyebrow} title="Warehouse" />
        <PageState loading={loading && !overview} error={error} onRetry={() => void load()} />
        {overview && (
          <div className="grid gap-4 px-8 max-md:px-4">
            <section className={PANEL} aria-label="Calendar">
              <OverviewCalendar items={items} today={today} readOnly />
            </section>
            <section aria-labelledby="warehouse-discrepancies">
              <h2 id="warehouse-discrepancies" className="px-1 pb-2 pt-2 font-display text-[15px] font-bold">Discrepancies</h2>
              <div data-queue-content="" data-queue="decide" className={QUEUE_CARD_CLASS}>
                <DiscrepanciesView overview={overview} readOnly />
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
